<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { getProductionOrder, getProductionOrderStages, listProductionLots } from '../productService'
import type { ProductionLot, ProductionOrder, ProductionOrderStage } from '../types'
import { findBottleneckStage, stageCompletionPercent, summarizeStageExceptions } from '../productionReporting'

const route = useRoute()
const client = useApiClient()
const { t } = useI18n()
const orderId = computed(() => Number(route.params.id))
const order = ref<ProductionOrder | null>(null)
const stages = ref<ProductionOrderStage[]>([])
const lots = ref<ProductionLot[]>([])
const loading = ref(false)
const error = ref('')

const bottleneck = computed(() => findBottleneckStage(stages.value))
const activeLots = computed(() => lots.value.filter((lot) => lot.status === 'active').length)
const exceptionSummary = computed(() => summarizeStageExceptions(stages.value))

function stagePercent(stage: ProductionOrderStage): number {
  return stageCompletionPercent(stage)
}

function stageLotCount(stageCode: string): number {
  return lots.value.filter((lot) => lot.current_stage_code === stageCode).length
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    ;[order.value, stages.value, lots.value] = await Promise.all([
      getProductionOrder(client, orderId.value),
      getProductionOrderStages(client, orderId.value),
      listProductionLots(client, orderId.value),
    ])
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back" :to="`/production-orders/${orderId}`">
      {{ t('engineering.productionOrder.backToOrder') }}
    </RouterLink>
    <div v-if="order" class="page-toolbar__actions">
      <RouterLink class="action secondary" :to="`/production-orders/${order.id}`">
        {{ t('engineering.productionOrder.orderEyebrow') }}
      </RouterLink>
      <RouterLink class="action primary" :to="`/production-orders/${order.id}/lots`">
        {{ t('engineering.productionOrder.openLotQueue') }}
      </RouterLink>
    </div>
  </div>

  <div v-if="loading" class="state">{{ t('engineering.productionOrder.loadingOrder') }}</div>
  <div v-else-if="error" class="state error">{{ error }}</div>
  <template v-else-if="order">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ order.mo_code }}</p>
        <h2>{{ t('engineering.productionOrder.stagesTitle') }}</h2>
        <p>{{ t('engineering.productionOrder.stagesDescription') }}</p>
      </div>
      <div class="summary-strip">
        <span>{{ t('engineering.productionOrder.activeLotsCount', { count: activeLots }) }}</span>
        <span>{{ t('engineering.productionOrder.rejected') }}: {{ exceptionSummary.rejected }}</span>
        <span>{{ t('engineering.productionOrder.rework') }}: {{ exceptionSummary.rework }}</span>
      </div>
    </header>

    <section v-if="!stages.length" class="empty">
      {{ t('engineering.productionOrder.noBomStages') }}
    </section>
    <section v-else class="board">
      <article
        v-for="stage in stages"
        :key="stage.id"
        class="stage-card"
        :class="{ 'stage-card--bottleneck': bottleneck?.id === stage.id }"
      >
        <div class="stage-card__header">
          <span class="sequence">{{ stage.operation_sequence }}</span>
          <div>
            <strong>{{ stage.stage_name }}</strong>
            <small>{{ stage.stage_code }}</small>
          </div>
          <span class="badge">{{ t(`engineering.state.${stage.status}`) }}</span>
        </div>

        <div class="metrics">
          <div>
            <span>{{ t('engineering.productionOrder.plan') }}</span>
            <strong>{{ stage.planned_quantity }}</strong>
          </div>
          <div>
            <span>{{ t('engineering.productionOrder.actual') }}</span>
            <strong>{{ stage.completed_quantity }}</strong>
          </div>
          <div>
            <span>{{ t('engineering.productionOrder.yield') }}</span>
            <strong>{{ stagePercent(stage) }}%</strong>
          </div>
          <div>
            <span>{{ t('engineering.productionOrder.activeLots') }}</span>
            <strong>{{ stageLotCount(stage.stage_code) }}</strong>
          </div>
        </div>

        <progress :value="stage.completed_quantity" :max="stage.planned_quantity || 1"></progress>

        <p class="stage-notes">
          {{
            stage.assigned_employee_name
              ? t('engineering.productionOrder.stageRejectedReworkEmployee', {
                  rejected: stage.rejected_quantity,
                  rework: stage.rework_quantity,
                  employee: stage.assigned_employee_name,
                })
              : t('engineering.productionOrder.stageRejectedRework', {
                  rejected: stage.rejected_quantity,
                  rework: stage.rework_quantity,
                })
          }}
        </p>
      </article>
    </section>
  </template>
</template>

<style scoped>
.page-toolbar,.page-toolbar__actions,.stage-card__header{display:flex;gap:12px;align-items:center;justify-content:space-between}
.back{display:inline-block;margin-bottom:20px;color:#285b9a;font-weight:700}
.action{padding:10px 14px;color:#fff;background:#3568a8;font-weight:700;text-decoration:none}
.action.secondary{color:#285b9a;background:#eaf1f8}
.action.primary{background:#07996f}
.eyebrow{margin:0;color:#66726e;font-size:12px;font-weight:800;text-transform:uppercase}
.page-heading{display:flex;gap:20px;align-items:flex-start;justify-content:space-between}
.page-heading h2{margin:4px 0}
.page-heading p:last-child{margin:0;color:#66726e}
.summary-strip{display:flex;gap:8px;flex-wrap:wrap}
.summary-strip span,.badge{padding:6px 9px;font-size:12px;font-weight:800}
.summary-strip span{color:#6b4b12;background:#fffae9}
.board{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px;margin-top:18px}
.stage-card,.empty{padding:18px;border:1px solid #d7dfdc;background:#fff}
.stage-card--bottleneck{border-color:#e2b65d;background:#fffae9}
.sequence{display:grid;width:30px;height:30px;color:#fff;background:#3568a8;place-items:center}
.badge{margin-left:auto;color:#236451;background:#eaf7f2;text-transform:capitalize}
.stage-card__header strong,.metrics strong{display:block}
.stage-card__header small,.metrics span,.stage-notes{color:#66726e}
.metrics{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-top:14px}
.metrics div{padding:12px;border:1px solid #e1e7e5;background:#f8faf9}
progress{width:100%;margin-top:14px}
.stage-notes{margin:10px 0 0;font-size:13px}
.state{display:grid;min-height:220px;place-items:center}
.error{color:#852d22}
@media (max-width: 800px){.page-heading,.page-toolbar,.page-toolbar__actions{align-items:stretch;flex-direction:column}}
</style>
